import React, { useEffect, useState } from 'react';
import { t } from '../../i18n';
import { Link } from 'react-router-dom';
import { realtyApi, realtyPiApi } from './realtyApi';

const STATUS_CLASS = { active: 'status-active', sold: 'status-sold', pending: 'status-pending', draft: 'status-pending', expired: 'status-pending' };

export default function RealtyDashboard() {
  const [tab, setTab] = useState('listings');
  const [listings, setListings] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [closingId, setClosingId] = useState(null);
  const [dealForm, setDealForm] = useState({ buyer_name: '', buyer_phone: '', final_price: '' });
  const [message, setMessage] = useState('');
  const [dealsByProperty, setDealsByProperty] = useState({});
  const [boostingId, setBoostingId] = useState(null);
  const [payingId, setPayingId] = useState(null);
  const [commissionInputs, setCommissionInputs] = useState({});

  function load() {
    setLoading(true);
    Promise.all([realtyApi.myProperties(), realtyApi.myInquiries()])
      .then(([props, inqs]) => {
        setListings(props.results || props);
        setInquiries(inqs.results || inqs);
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => { load(); }, []);

  async function handleCloseDeal(id) {
    try {
      const property = listings.find((p) => p.id === id);
      const deal = await realtyApi.closeDeal(id, { ...dealForm, final_price: dealForm.final_price || property.price });
      setDealsByProperty((prev) => ({ ...prev, [id]: deal }));
      setMessage('Deal closed. 5% platform commission has been calculated — pay it via Pi below.');
      setClosingId(null);
      setDealForm({ buyer_name: '', buyer_phone: '', final_price: '' });
      load();
    } catch (e) {
      setMessage('Could not close deal: ' + e.message);
    }
  }

  async function payWithPi(amountPi, purpose, extra, onDone) {
    if (!window.Pi) {
      setMessage('Open this page inside Pi Browser to pay with Pi.');
      return;
    }
    try {
      await window.Pi.authenticate(['username', 'payments'], () => {});
      window.Pi.createPayment(
        {
          amount: amountPi,
          memo: purpose === 'featured_listing' ? 'Boost listing - 7 days' : 'XpertRealty deal commission',
          metadata: extra,
        },
        {
          onReadyForServerApproval: (paymentId) => {
            realtyPiApi.approve({ paymentId, purpose, amount: amountPi, ...extra });
          },
          onReadyForServerCompletion: (paymentId, txid) => {
            realtyPiApi.complete({ paymentId, txid })
              .then(() => {
                setMessage('Payment complete!');
                onDone && onDone();
                load();
              })
              .catch(() => setMessage('Payment reached Pi but activation failed. Contact support.'));
          },
          onCancel: () => setMessage('Payment cancelled.'),
          onError: () => setMessage('Payment error.'),
        }
      );
    } catch (e) {
      setMessage('Could not get Pi payment permission.');
    }
  }

  function boostListing(id) {
    setBoostingId(id);
    payWithPi(1, 'featured_listing', { propertyId: id }, () => setBoostingId(null));
  }

  function payCommission(propertyId, deal) {
    const amount = parseFloat(commissionInputs[propertyId]);
    if (!amount || amount <= 0) {
      setMessage('Please enter how much Pi this commission equals before paying.');
      return;
    }
    setPayingId(propertyId);
    payWithPi(amount, 'deal_commission', { dealId: deal.id, propertyId }, () => setPayingId(null));
  }

  return (
    <div className="realty-scope container section">
      <h2 style={{ marginBottom: 4 }}>{t('your_dashboard')}</h2>
      <p className="muted" style={{ marginBottom: 20 }}>{t('manage_listings_desc')}</p>

      {message && <div className="success-box">{message}</div>}

      <div className="dash-tabs">
        <button className={tab === 'listings' ? 'active' : ''} onClick={() => setTab('listings')}>My Listings ({listings.length})</button>
        <button className={tab === 'inquiries' ? 'active' : ''} onClick={() => setTab('inquiries')}>Inquiries ({inquiries.length})</button>
      </div>

      {loading && <p className="muted">{t('loading')}</p>}

      {!loading && tab === 'listings' && (
        listings.length === 0 ? (
          <div className="empty-state">
            <h3>{t('no_listings_yet')}</h3>
            <p className="muted">{t('post_first_property')}</p>
            <Link to="/realty/post-ad" className="btn btn-primary" style={{ marginTop: 14, display: 'inline-block' }}>{t('post_property_btn')}</Link>
          </div>
        ) : listings.map((p) => {
          const deal = dealsByProperty[p.id];
          return (
            <div key={p.id} className="dash-row" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <Link to={`/realty/property/${p.id}`} style={{ fontWeight: 600 }}>{p.title}</Link>
                  <div className="muted" style={{ fontSize: 13 }}>{p.city} · Rs {Number(p.price).toLocaleString()}</div>
                </div>
                <span className={`status-pill ${STATUS_CLASS[p.status] || 'status-pending'}`}>{p.status}</span>
              </div>

              {p.status === 'active' && !p.is_featured && (
                <button
                  className="btn btn-outline"
                  style={{ marginTop: 10, alignSelf: 'flex-start' }}
                  disabled={boostingId === p.id}
                  onClick={() => boostListing(p.id)}
                >
                  {boostingId === p.id ? 'Processing...' : '📢 Boost this listing (1 Pi / 7 days)'}
                </button>
              )}
              {p.is_featured && (
                <span className="muted" style={{ marginTop: 8, fontSize: 12 }}>📢 Currently boosted</span>
              )}

              {p.status === 'active' && (
                closingId === p.id ? (
                  <div style={{ marginTop: 12, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
                    <div className="field-row">
                      <div className="field">
                        <label>{t('buyer_name')}</label>
                        <input value={dealForm.buyer_name} onChange={(e) => setDealForm({ ...dealForm, buyer_name: e.target.value })} />
                      </div>
                      <div className="field">
                        <label>{t('buyer_phone')}</label>
                        <input value={dealForm.buyer_phone} onChange={(e) => setDealForm({ ...dealForm, buyer_phone: e.target.value })} />
                      </div>
                    </div>
                    <div className="field">
                      <label>Final agreed price (leave blank to use listed price)</label>
                      <input type="number" value={dealForm.final_price} onChange={(e) => setDealForm({ ...dealForm, final_price: e.target.value })} />
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button className="btn btn-primary" onClick={() => handleCloseDeal(p.id)}>{t('confirm_deal_closed')}</button>
                      <button className="btn btn-outline" onClick={() => setClosingId(null)}>{t('cancel')}</button>
                    </div>
                  </div>
                ) : (
                  <button className="btn btn-outline" style={{ marginTop: 10, alignSelf: 'flex-start' }} onClick={() => setClosingId(p.id)}>
                    Mark as deal closed
                  </button>
                )
              )}

              {deal && deal.status === 'confirmed' && (
                <div style={{ marginTop: 12, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
                  <p style={{ fontSize: 14, marginBottom: 8 }}>
                    Commission due: <b>Rs {Number(deal.commission_amount).toLocaleString()}</b> (5% of Rs {Number(deal.final_price).toLocaleString()})
                  </p>
                  <div className="field-row">
                    <div className="field">
                      <label>Pi amount to pay (enter today's equivalent)</label>
                      <input
                        type="number"
                        step="0.000001"
                        placeholder="e.g. 50"
                        value={commissionInputs[p.id] || ''}
                        onChange={(e) => setCommissionInputs((prev) => ({ ...prev, [p.id]: e.target.value }))}
                      />
                    </div>
                  </div>
                  <button
                    className="btn btn-primary"
                    disabled={payingId === p.id}
                    onClick={() => payCommission(p.id, deal)}
                  >
                    {payingId === p.id ? 'Processing...' : 'Pay commission via Pi'}
                  </button>
                </div>
              )}
              {deal && deal.status === 'commission_paid' && (
                <p className="muted" style={{ marginTop: 8, fontSize: 12 }}>✅ Commission paid</p>
              )}
            </div>
          );
        })
      )}

      {!loading && tab === 'inquiries' && (
        inquiries.length === 0 ? (
          <div className="empty-state"><h3>{t('no_inquiries_yet')}</h3></div>
        ) : inquiries.map((i) => (
          <div key={i.id} className="dash-row">
            <div>
              <div style={{ fontWeight: 600 }}>{i.name} — {i.phone}</div>
              <div className="muted" style={{ fontSize: 13 }}>{i.message || 'No message'}</div>
            </div>
            <span className="muted" style={{ fontSize: 12 }}>{new Date(i.created_at).toLocaleDateString()}</span>
          </div>
        ))
      )}
    </div>
  );
}
